import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import SearchInput from '../components/SearchInput';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { BriefcaseBusiness, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const CustomerTerritoriesPage = () => {
  const { isAdmin } = useAuth();
  const [links, setLinks] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [territories, setTerritories] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({ customerId: '', territoryId: '', minimumOrderValue: '', deviceType: '' });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const loadData = async () => {
    try {
      const [linkResponse, customerResponse, territoryResponse] = await Promise.all([
        api.getCustomerTerritories({ page: 1, limit: 100, search }),
        api.getCustomers({ page: 1, limit: 100 }),
        api.getTerritories({ page: 1, limit: 100 }),
      ]);
      setLinks(linkResponse.data || []);
      setCustomers(customerResponse.data || []);
      setTerritories(territoryResponse.data || []);
    } catch (error) {
      toast.error('Erro ao carregar clientes por carteira');
    }
  };

  const save = async (event) => {
    event.preventDefault();
    try {
      await api.createCustomerTerritory({
        ...formData,
        minimumOrderValue: Number(formData.minimumOrderValue || 0),
        deviceType: formData.deviceType || undefined,
      });
      toast.success('Cliente vinculado à carteira');
      setDialogOpen(false);
      setFormData({ customerId: '', territoryId: '', minimumOrderValue: '', deviceType: '' });
      loadData();
    } catch (error) {
      toast.error(error.message || 'Erro ao vincular cliente');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Remover cliente desta carteira?')) return;
    try {
      await api.deleteCustomerTerritory(id);
      toast.success('Vínculo removido');
      loadData();
    } catch (error) {
      toast.error('Erro ao remover vínculo');
    }
  };

  return (
    <div className="space-y-6" data-testid="customer-territories-page">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Clientes por Carteira</h1>
          <p className="text-muted-foreground mt-1">Vínculo de clientes com carteiras comerciais</p>
        </div>
        {isAdmin() && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Vincular Cliente
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Novo Vínculo</DialogTitle>
              </DialogHeader>
              <form onSubmit={save} className="space-y-4">
                <div className="space-y-2">
                  <Label>Cliente</Label>
                  <Select value={formData.customerId} onValueChange={(value) => setFormData({ ...formData, customerId: value })}>
                    <SelectTrigger><SelectValue placeholder="Selecione o cliente" /></SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => <SelectItem key={customer.id} value={customer.id}>{customer.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Carteira</Label>
                  <Select value={formData.territoryId} onValueChange={(value) => setFormData({ ...formData, territoryId: value })}>
                    <SelectTrigger><SelectValue placeholder="Selecione a carteira" /></SelectTrigger>
                    <SelectContent>
                      {territories.map((territory) => <SelectItem key={territory.id} value={territory.id}>{territory.code} - {territory.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Valor mínimo</Label>
                    <Input type="number" min="0" step="0.01" value={formData.minimumOrderValue} onChange={(e) => setFormData({ ...formData, minimumOrderValue: e.target.value })} />
                  </div>
                  <div className="space-y-2">
                    <Label>Dispositivo</Label>
                    <Input value={formData.deviceType} onChange={(e) => setFormData({ ...formData, deviceType: e.target.value })} />
                  </div>
                </div>
                <Button type="submit" className="w-full">Salvar Vínculo</Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <SearchInput value={search} onChange={setSearch} placeholder="Pesquisar por cliente, documento ou carteira..." searchFields={['Cliente', 'Documento', 'Carteira']} />

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-6 py-3 text-left text-sm font-semibold">Cliente</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Documento</th>
                <th className="px-6 py-3 text-left text-sm font-semibold">Carteira</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Valor mínimo</th>
                <th className="px-6 py-3 text-center text-sm font-semibold">Status</th>
                <th className="px-6 py-3 text-right text-sm font-semibold">Ações</th>
              </tr>
            </thead>
            <tbody>
              {links.map((link) => (
                <tr key={link.id} className="border-b hover:bg-muted/50">
                  <td className="px-6 py-4 text-sm font-medium">{link.customer?.name}</td>
                  <td className="px-6 py-4 text-sm font-mono">{link.customer?.document}</td>
                  <td className="px-6 py-4 text-sm">{link.territory?.code} - {link.territory?.name}</td>
                  <td className="px-6 py-4 text-sm text-right">R$ {Number(link.minimumOrderValue || 0).toFixed(2)}</td>
                  <td className="px-6 py-4 text-sm text-center">
                    {isAdmin() ? <Switch checked={link.enabled} onCheckedChange={() => api.toggleCustomerTerritory(link.id).then(loadData)} /> : <Badge>{link.enabled ? 'Ativo' : 'Inativo'}</Badge>}
                  </td>
                  <td className="px-6 py-4 text-sm text-right">
                    {isAdmin() && <Button variant="ghost" size="sm" onClick={() => remove(link.id)}><Trash2 className="w-4 h-4 text-destructive" /></Button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {links.length === 0 && (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <BriefcaseBusiness className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Nenhuma carteira encontrada.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default CustomerTerritoriesPage;
